import { Box, Typography } from "@mui/material"
import React, { useCallback, useContext } from "react"
import { customPalette } from "../../../../../../../../theme"
import {
  BOTTOM_NAVIGATION_HEIGHT,
  CustomBottomNavigation,
} from "../../../../../../../components/buttons/navigate-button/CustomBottomNavigation"
import { SuccessContext } from "../../../../../../../components/layout/success-snackbar"
import { BSVariantContext } from "../../../../../../../core/context/beem-shot/BSVariant/BSVariantContext"
import { ProjectContext } from "../../../../../../../core/context/project/ProjectContext"
import { BSVariant } from "../../../../../../../core/dto/beem-shot/BSVariant/BSVariant"
import { BSVariantParcellePatchDto } from "../../../../../../../core/dto/beem-shot/BSVariant/BSVariantParcellePatchDto"
import { CalculStatusEnum } from "../../../../../../../core/enum/calculStatusEnum"
import { useForm } from "../../../../../../../core/hooks/form/use-form"
import { nonZero, required } from "../../../../../../../core/hooks/form/validation"
import { formatToFrench } from "../../../../../../../core/services/date-service"
import { BSBuildingListContext } from "../../building/components/context/BSBuildingListContext"
import { BSVariantControlPanelContext } from "../../components/BSVariantControlPanelContext"
import { BSVariantFormCard } from "../../components/BSVariantFormCard"

interface IForm {
  surfaceParcelle: number
}

function formToDtoUpdate(form: IForm, variantId: string): BSVariantParcellePatchDto {
  return {
    id: variantId,
    surfaceParcelle: form.surfaceParcelle,
  }
}

function dtoToForm(dto: BSVariant | undefined): IForm {
  return dto
    ? {
        surfaceParcelle: dto.surfaceParcelle,
      }
    : {
        surfaceParcelle: 0,
      }
}

interface IProps {
  bsVariant?: BSVariant
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>
  isUpdating: boolean
}

export function BSVariantParcelleTab({ bsVariant, isUpdating, setIsUpdating }: Readonly<IProps>): React.JSX.Element {
  const openSuccessSnackbar = useContext(SuccessContext)
  const { updateBSVariantParcelle } = useContext(BSVariantContext)
  const { project } = useContext(ProjectContext)
  const { controlPanelRefWidth } = useContext(BSVariantControlPanelContext)
  const { updateAllBSBuildingStatusState } = useContext(BSBuildingListContext)

  const submit: (form: IForm) => Promise<any> = useCallback(
    (form: IForm) => {
      if (bsVariant?.id) {
        return updateBSVariantParcelle(formToDtoUpdate(form, bsVariant.id)).then(() => {
          updateAllBSBuildingStatusState(CalculStatusEnum.WAITING_FOR_CONFIGURATION)
          setIsUpdating(false)
          openSuccessSnackbar("Les informations de l'estimation ont été mises à jour.")
        })
      }
      return Promise.resolve()
    },
    [bsVariant, openSuccessSnackbar, updateBSVariantParcelle]
  )

  const { form, errors, handleChange, handleSubmit, isSubmitting } = useForm(
    bsVariant,
    dtoToForm,
    [required("surfaceParcelle"), nonZero("surfaceParcelle")],
    submit
  )

  if (!bsVariant) {
    return <Typography>Aucune estimation sélectionnée</Typography>
  }

  return (
    <Box
      component="form"
      id="bs-variant-update-parcelle"
      onSubmit={handleSubmit}
      height={`calc(100% - ${BOTTOM_NAVIGATION_HEIGHT} -  2vh)`}
      px={5}
      sx={{ overflow: "auto" }}>
      <Typography variant="h5" fontWeight={600} sx={{ mt: 2 }}>
        Parcelle
      </Typography>

      <Typography fontSize={10} fontWeight={400} sx={{ color: customPalette.lightGray4, mb: 2 }}>
        {` Mise à jour le ${formatToFrench(project?.lastModifiedDate)}`}
      </Typography>

      <BSVariantFormCard
        id="surfaceParcelle"
        label="Surface totale en m²"
        errors={errors}
        form={form}
        handleChange={(e) => {
          setIsUpdating(true)
          handleChange(e)
        }}
        subTitle="Les informations de la parcelle sont nécessaires pour estimer l'impact carbone."
      />

      {isUpdating && (
        <CustomBottomNavigation
          actionLabel="Enregistrer"
          formId="bs-variant-update-parcelle"
          isSubmitting={isSubmitting}
          additionalLeftPadding={controlPanelRefWidth ?? 0}
        />
      )}
    </Box>
  )
}
